import { FC, useLayoutEffect, useRef, useState } from 'react';

import {
  useLayout,
  useProcessedBannerStore,
  useProcessedProblems,
} from '../../../studentApp/hooks';
import { NormalProblem } from '../../../studentApp/stores';
import { Hand } from '../banner/hand';

import { CurrentSkillDetails } from './currentSkillDetails';
import { NeedHelpButton } from './needHelpButton';
import { ShowSquares } from './showSquares';

export const NormalTop: FC = () => {
  const { isShowHand } = useProcessedBannerStore();
  const { isMobile } = useLayout();
  const problem = useProcessedProblems().currentProblem as
    | NormalProblem
    | undefined;
  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const middleRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [wrapSkill, setWrapSkill] = useState(false);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const left = leftRef.current;
    const middle = middleRef.current;
    const right = rightRef.current;

    if (!header || !left || !middle || !right) return;

    const updateLayout = (): void => {
      // Centering needs equal room on both sides, including the wider controls.
      const sideWidth = Math.max(
        left.getBoundingClientRect().width,
        right.getBoundingClientRect().width
      );
      const requiredWidth =
        middle.getBoundingClientRect().width + 2 * (sideWidth + 8);

      setWrapSkill(header.getBoundingClientRect().width < requiredWidth);
    };
    let animationFrame = 0;
    const scheduleLayout = (): void => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateLayout);
    };
    const observer =
      typeof ResizeObserver === 'undefined'
        ? new MutationObserver(scheduleLayout)
        : new ResizeObserver(scheduleLayout);

    if (observer instanceof MutationObserver) {
      observer.observe(header, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
      window.addEventListener('resize', scheduleLayout);
    } else {
      [header, left, middle, right].forEach(element =>
        observer.observe(element)
      );
    }

    updateLayout();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleLayout);
      cancelAnimationFrame(animationFrame);
    };
  }, [problem]);

  if (!problem) return null;

  const hideNeedHelp = problem.type === 'EXAM_CORRECTIONS';

  return (
    <div
      ref={headerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        columnGap: '8px',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {/* Left Section */}
      <div
        ref={leftRef}
        style={{
          display: 'flex',
          width: 'max-content',
          gridColumn: 1,
          gridRow: 1,
          justifySelf: 'start',
        }}
      >
        <ShowSquares />
      </div>

      {/* Middle Section */}
      <div
        ref={middleRef}
        style={{
          display: 'flex',
          width: 'max-content',
          gridColumn: wrapSkill ? '1 / -1' : 2,
          gridRow: wrapSkill ? 2 : 1,
          justifySelf: 'center',
        }}
      >
        <CurrentSkillDetails />
      </div>

      {/* Right Section */}
      <div
        ref={rightRef}
        style={{
          display: 'flex',
          flex: '0 0 auto',
          width: 'max-content',
          gridColumn: 3,
          gridRow: 1,
          justifySelf: 'end',
          justifyContent: 'flex-end',
        }}
      >
        {!hideNeedHelp && <NeedHelpButton />}
        {!isMobile && isShowHand && <Hand />}
      </div>
    </div>
  );
};
